<!-- 库存智能预警 -->
<template>
  <div class="inventory-warning-page">
    <section class="filter-panel">
      <el-form :model="queryForm" size="small" label-width="76px" class="filter-form">
        <el-form-item label="统计时间">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            :default-time="['00:00:00', '23:59:59']"
            unlink-panels
            clearable
          />
        </el-form-item>
        <el-form-item label="客户">
          <el-select
            v-model="queryForm.custNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCustomerOptions"
            :loading="customerLoading"
            placeholder="全部客户"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.id"
              :label="item.deptName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种">
          <el-select
            v-model="queryForm.cargoNo"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="loadCargoOptions"
            :loading="cargoLoading"
            placeholder="全部煤种"
          >
            <el-option
              v-for="item in cargoOptions"
              :key="item.varNo || item.id"
              :label="item.varName"
              :value="item.varNo || item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="仓库编码">
          <el-input v-model.trim="queryForm.stockCode" clearable placeholder="全部仓库" />
        </el-form-item>
        <el-form-item label="库位编码">
          <el-input v-model.trim="queryForm.locationCode" clearable placeholder="全部库位" />
        </el-form-item>
        <el-form-item label="预警等级">
          <el-select v-model="queryForm.warningLevel" clearable placeholder="全部等级">
            <el-option v-for="item in levelOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="预警类型">
          <el-select v-model="queryForm.warningType" clearable placeholder="全部类型">
            <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="车牌号">
          <el-input v-model.trim="queryForm.trainNumber" clearable placeholder="请输入车牌号" />
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" :loading="pageLoading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          <el-button type="warning" plain icon="el-icon-setting" @click="ruleVisible = true">规则配置</el-button>
        </el-form-item>
      </el-form>
    </section>

    <el-alert
      v-if="pageError"
      class="state-alert"
      :title="pageError"
      type="error"
      show-icon
      :closable="false"
    />

    <section v-loading="summaryLoading" class="summary-grid">
      <div v-for="card in summaryCards" :key="card.key" :class="['metric-card', card.accent]">
        <div class="metric-head">
          <span>{{ card.label }}</span>
          <span>{{ card.caption }}</span>
        </div>
        <div class="metric-value">
          {{ card.value }}<small>{{ card.unit }}</small>
        </div>
        <div class="metric-foot">{{ card.sub }}</div>
      </div>
    </section>

    <section class="analysis-grid">
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>预警类型分布</span>
          <span>{{ summary.scanTime || '--' }}</span>
        </div>
        <div ref="typeChart" class="chart-box" />
      </div>
      <div v-loading="summaryLoading" class="panel">
        <div class="panel-head">
          <span>智能处置建议</span>
          <span>按风险优先级</span>
        </div>
        <div v-if="suggestions.length" class="suggestion-list">
          <div v-for="item in suggestions" :key="item.title" :class="['suggestion-item', item.type]">
            <div>
              <strong>{{ item.title }}</strong>
              <el-tag size="mini" :type="suggestionTagType(item.type)">{{ item.tag }}</el-tag>
            </div>
            <p>{{ item.body }}</p>
          </div>
        </div>
        <el-empty v-else :image-size="80" description="暂无处置建议" />
      </div>
    </section>

    <section class="table-panel">
      <div class="panel-head table-head">
        <span>预警明细</span>
        <span>汽运出库记录</span>
      </div>
      <el-table
        v-loading="pageLoading"
        :data="records"
        border
        stripe
        size="small"
        empty-text="当前筛选条件下暂无预警"
        style="width: 100%"
      >
        <el-table-column prop="warningLevelName" label="等级" width="76" fixed align="center">
          <template slot-scope="{ row }">
            <el-tag :type="levelTagType(row.warningLevel)" size="small">{{ row.warningLevelName || '--' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="预警类型" width="104" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.warningTypeName || '--' }}</template>
        </el-table-column>
        <el-table-column label="客户" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.custName || row.custNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="煤种" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.cargoName || row.cargoNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="仓库/库位" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ warehouseText(row) }}</template>
        </el-table-column>
        <el-table-column label="当前库存" width="118" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.currentInventory) }}</template>
        </el-table-column>
        <el-table-column label="净重" width="104" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.netWeight) }}</template>
        </el-table-column>
        <el-table-column prop="trainNumber" label="车牌号" width="112" show-overflow-tooltip />
        <el-table-column prop="lastOutTime" label="最近出库" width="155" show-overflow-tooltip />
        <el-table-column label="未出库天数" width="96" align="right">
          <template slot-scope="{ row }">{{ row.daysSinceLastOut === null || row.daysSinceLastOut === undefined ? '--' : row.daysSinceLastOut }}</template>
        </el-table-column>
        <el-table-column prop="reason" label="智能原因" min-width="220" show-overflow-tooltip />
        <el-table-column prop="action" label="建议动作" min-width="150" show-overflow-tooltip />
      </el-table>
      <el-pagination
        class="page-bar"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="page.currentPage"
        :page-size.sync="page.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="page.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </section>

    <el-drawer
      title="规则配置"
      :visible.sync="ruleVisible"
      direction="rtl"
      size="360px"
      custom-class="rule-drawer"
    >
      <el-form :model="ruleForm" label-width="124px" size="small" class="rule-form">
        <el-form-item label="低库存阈值">
          <el-input-number v-model="ruleForm.lowStockThreshold" :min="0" :precision="2" controls-position="right" />
        </el-form-item>
        <el-form-item label="高库存阈值">
          <el-input-number v-model="ruleForm.highStockThreshold" :min="0" :precision="2" controls-position="right" />
        </el-form-item>
        <el-form-item label="周转异常天数">
          <el-input-number v-model="ruleForm.slowTurnoverDays" :min="1" :precision="0" controls-position="right" />
        </el-form-item>
        <el-form-item label="异常净重判断">
          <el-switch v-model="ruleForm.netWeightAbnormalEnabled" active-text="启用" inactive-text="关闭" />
        </el-form-item>
        <div class="drawer-actions">
          <el-button type="primary" icon="el-icon-check" @click="applyRules">应用</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetRules">恢复默认</el-button>
        </div>
      </el-form>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchSummary, fetchPage, fetchCustomerOptions, fetchCargoOptions } from './api'

const DEFAULT_RULES = {
  lowStockThreshold: 100,
  highStockThreshold: 1000,
  slowTurnoverDays: 5,
  netWeightAbnormalEnabled: true
}

export default {
  name: 'IntelligentInventoryWarning',
  data() {
    return {
      dateRange: [],
      queryForm: {
        custNo: '',
        cargoNo: '',
        stockCode: '',
        locationCode: '',
        warningLevel: '',
        warningType: '',
        trainNumber: ''
      },
      ruleForm: { ...DEFAULT_RULES },
      ruleVisible: false,
      summaryLoading: false,
      pageLoading: false,
      customerLoading: false,
      cargoLoading: false,
      pageError: '',
      summary: {},
      records: [],
      customerOptions: [],
      cargoOptions: [],
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      chart: null,
      levelOptions: [
        { label: '高', value: 'high' },
        { label: '中', value: 'medium' },
        { label: '低', value: 'low' }
      ],
      typeOptions: [
        { label: '低库存', value: 'low_stock' },
        { label: '高库存', value: 'high_stock' },
        { label: '周转异常', value: 'slow_turnover' },
        { label: '数据异常', value: 'data_abnormal' }
      ]
    }
  },
  computed: {
    summaryCards() {
      return [
        {
          key: 'risk',
          label: '风险库存',
          caption: '实时扫描',
          value: this.number(this.summary.totalRiskCount),
          unit: '项',
          sub: `高 ${this.number(this.summary.highRiskCount)} / 中 ${this.number(this.summary.mediumRiskCount)}`,
          accent: 'danger'
        },
        {
          key: 'hit',
          label: '预警命中率',
          caption: '规则命中',
          value: this.number(this.summary.hitRate),
          unit: '%',
          sub: `扫描 ${this.number(this.summary.totalCount)} 条出库记录`,
          accent: 'warn'
        },
        {
          key: 'weight',
          label: '出库净重',
          caption: 'businessType=2',
          value: this.formatNumber(this.summary.totalNetWeight),
          unit: '吨',
          sub: '按当前筛选范围汇总',
          accent: 'primary'
        },
        {
          key: 'health',
          label: '健康评分',
          caption: '智能诊断',
          value: this.number(this.summary.healthScore),
          unit: '分',
          sub: this.summary.healthScore >= 80 ? '风险可控' : '建议重点复核',
          accent: this.summary.healthScore >= 80 ? 'ok' : 'danger'
        }
      ]
    },
    suggestions() {
      return this.summary.suggestions || []
    }
  },
  mounted() {
    this.setDefaultDateRange()
    this.loadCustomerOptions('')
    this.loadCargoOptions('')
    this.loadData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    setDefaultDateRange() {
      const end = new Date()
      const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000)
      this.dateRange = [`${this.formatDate(start)} 00:00:00`, `${this.formatDate(end)} 23:59:59`]
    },
    buildParams() {
      const params = {
        ...this.queryForm,
        ...this.ruleForm
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }
      return params
    },
    async loadData() {
      this.summaryLoading = true
      this.pageLoading = true
      this.pageError = ''
      const params = this.buildParams()
      const pageParams = {
        ...params,
        page: this.page.currentPage,
        size: this.page.pageSize,
        onlyRisk: true
      }
      try {
        const [summaryRes, pageRes] = await Promise.all([fetchSummary(params), fetchPage(pageParams)])
        this.summary = (summaryRes.data && summaryRes.data.data) || {}
        const pageData = (pageRes.data && pageRes.data.data) || {}
        this.records = pageData.records || []
        this.page.total = Number(pageData.total || 0)
        this.$nextTick(this.renderTypeChart)
      } catch (e) {
        this.summary = {}
        this.records = []
        this.page.total = 0
        this.pageError = '库存智能预警数据加载失败，请稍后重试'
        this.$nextTick(this.renderTypeChart)
      } finally {
        this.summaryLoading = false
        this.pageLoading = false
      }
    },
    handleSearch() {
      this.page.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.queryForm = {
        custNo: '',
        cargoNo: '',
        stockCode: '',
        locationCode: '',
        warningLevel: '',
        warningType: '',
        trainNumber: ''
      }
      this.setDefaultDateRange()
      this.handleSearch()
    },
    applyRules() {
      this.ruleVisible = false
      this.handleSearch()
    },
    resetRules() {
      this.ruleForm = { ...DEFAULT_RULES }
    },
    handleSizeChange(size) {
      this.page.pageSize = size
      this.page.currentPage = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.page.currentPage = page
      this.loadData()
    },
    async loadCustomerOptions(query) {
      this.customerLoading = true
      try {
        const res = await fetchCustomerOptions(query ? { deptName: query } : {})
        this.customerOptions = (res.data && res.data.data) || []
      } finally {
        this.customerLoading = false
      }
    },
    async loadCargoOptions(query) {
      this.cargoLoading = true
      try {
        const res = await fetchCargoOptions(query ? { varName: query } : {})
        this.cargoOptions = (res.data && res.data.data) || []
      } finally {
        this.cargoLoading = false
      }
    },
    renderTypeChart() {
      const el = this.$refs.typeChart
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      const rows = this.summary.typeDistribution || []
      this.chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 36, right: 18, top: 20, bottom: 28 },
        xAxis: {
          type: 'category',
          data: rows.map(item => item.name),
          axisTick: { show: false }
        },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          {
            type: 'bar',
            barWidth: 28,
            data: rows.map(item => ({
              value: item.count || 0,
              itemStyle: { color: this.warningColor(item.level) }
            }))
          }
        ]
      })
    },
    resizeChart() {
      if (this.chart) this.chart.resize()
    },
    warningColor(level) {
      if (level === 'high') return '#f56c6c'
      if (level === 'medium') return '#e6a23c'
      return '#409eff'
    },
    levelTagType(level) {
      if (level === 'high') return 'danger'
      if (level === 'medium') return 'warning'
      return 'info'
    },
    suggestionTagType(type) {
      if (type === 'danger') return 'danger'
      if (type === 'warn') return 'warning'
      if (type === 'ok') return 'success'
      return 'info'
    },
    warehouseText(row) {
      const stock = row.stockName || row.stockCode || '--'
      const location = row.locationName || row.locationCode || '--'
      return `${stock} / ${location}`
    },
    formatWeight(value) {
      if (value === null || value === undefined || value === '') return '--'
      return `${this.formatNumber(value)} 吨`
    },
    number(value) {
      return Number(value || 0)
    },
    formatNumber(value) {
      const num = Number(value || 0)
      return num.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
  }
}
</script>

<style scoped lang="scss">
.inventory-warning-page {
  padding: 12px;
  background: #f3f5f8;
  min-height: 100%;
}

.filter-panel,
.panel,
.table-panel {
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 4px;
}

.filter-panel {
  padding: 12px 12px 2px;
  margin-bottom: 12px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;

  ::v-deep .el-form-item {
    margin-right: 12px;
    margin-bottom: 10px;
  }

  ::v-deep .el-date-editor--datetimerange {
    width: 330px;
  }

  ::v-deep .el-select,
  ::v-deep .el-input {
    width: 170px;
  }
}

.filter-actions {
  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    white-space: nowrap;
  }
}

.state-alert {
  margin-bottom: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.metric-card {
  min-height: 96px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 4px;
  border-top: 3px solid #409eff;
}

.metric-card.warn {
  border-top-color: #e6a23c;
}

.metric-card.danger {
  border-top-color: #f56c6c;
}

.metric-card.ok {
  border-top-color: #67c23a;
}

.metric-head {
  display: flex;
  justify-content: space-between;
  color: #606266;

  span:last-child {
    color: #909399;
    font-size: 12px;
  }
}

.metric-value {
  margin-top: 10px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  color: #303133;

  small {
    margin-left: 4px;
    font-size: 12px;
    color: #909399;
    font-weight: 400;
  }
}

.metric-foot {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.panel {
  padding: 12px;
  min-height: 250px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 600;
  color: #303133;

  span:last-child {
    color: #909399;
    font-size: 12px;
    font-weight: 400;
  }
}

.chart-box {
  height: 190px;
}

.suggestion-list {
  display: grid;
  gap: 10px;
}

.suggestion-item {
  padding: 10px;
  border-left: 3px solid #409eff;
  background: #f8fbff;

  &.danger {
    border-left-color: #f56c6c;
    background: #fff7f7;
  }

  &.warn {
    border-left-color: #e6a23c;
    background: #fffaf0;
  }

  &.ok {
    border-left-color: #67c23a;
    background: #f6fff7;
  }

  div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  strong {
    color: #303133;
  }

  p {
    margin: 6px 0 0;
    color: #606266;
    line-height: 20px;
  }
}

.table-panel {
  padding: 12px;
}

.table-head {
  margin-bottom: 8px;
}

.page-bar {
  margin-top: 12px;
  text-align: right;
}

.rule-form {
  padding: 0 16px;

  ::v-deep .el-input-number {
    width: 180px;
  }
}

.drawer-actions {
  padding-left: 124px;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .analysis-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .inventory-warning-page {
    padding: 8px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .filter-form {
    display: block;

    ::v-deep .el-form-item {
      margin-right: 0;
    }

    ::v-deep .el-date-editor--datetimerange,
    ::v-deep .el-select,
    ::v-deep .el-input {
      width: 100%;
    }
  }
}
</style>
